/* eslint-disable css-modules/no-undef-class */
import { useCallback, useEffect, useState } from 'react';
import { throttle } from '@/libraries/throttle';
import breakpoints from '@/styles/modules/breakpoints.module.scss';
import { useSiteSettingsContext } from '../contexts/site-settings-context';

type Breakpoint = 'mobile' | 'tablet' | 'laptop' | 'desktop';

/**
 * Get the window size and check if it is within a certain breakpoint range,
 * default value is based off of user agent, if none is present it defaults to true
 * @param breakpoint The breakpoint to check against
 * @param toBreakpoint The breakpoint to check against
 * @returns Whether the window size is within the breakpoint
 */
export function useWindowSize(
  breakpoint: Breakpoint,
  toBreakpoint?: Breakpoint,
) {
  const { userAgent } = useSiteSettingsContext();
  const [isWithinLimit, setIsWithinLimit] = useState(() => {
    // If no user agent or if agent is a bot return false
    if (!userAgent || userAgent.isBot) return false;

    const { device } = userAgent;

    // If no device type, default to false
    if (!device.type) return false;

    // If user agent type is mobile and breakpoint is mobile, return true
    if (device.type === 'mobile') return breakpoint === 'mobile';

    // If user agent type is tablet and breakpoint is mobile or tablet, return true
    if (device.type === 'tablet')
      return ['tablet', 'mobile'].includes(breakpoint);

    // If none of the above conditions are met we default to largest breakpoint -> true
    return true;
  });

  const onResizeHandler = useCallback(() => {
    if (userAgent?.isBot) return;

    if (typeof window === 'undefined') return;

    const minWidth = parseInt(breakpoints[breakpoint]);
    const maxWidth = toBreakpoint
      ? parseInt(breakpoints[toBreakpoint])
      : undefined;

    if (maxWidth) {
      setIsWithinLimit(
        minWidth <= window.innerWidth && window.innerWidth <= maxWidth,
      );
    } else {
      setIsWithinLimit(minWidth <= window.innerWidth);
    }
  }, [breakpoint, toBreakpoint, userAgent]);

  useEffect(() => {
    onResizeHandler();
  }, [onResizeHandler]);

  const throttledHandler = throttle(onResizeHandler, 50);

  useEffect(() => {
    if (window !== undefined) {
      window.addEventListener('resize', throttledHandler);

      return () => {
        window.removeEventListener('resize', throttledHandler);
      };
    }
  }, [throttledHandler]);

  return isWithinLimit;
}
